import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  SITE_NAME,
  breadcrumbJsonLd,
  canonicalUrl,
  organizationJsonLd,
  publicOrigin,
  resolveSeo,
  serviceJsonLd,
  websiteJsonLd,
} from '../content/seo';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string, extra?: Record<string, string>) {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  if (extra) {
    Object.entries(extra).forEach(([k, v]) => el!.setAttribute(k, v));
  }
}

function upsertJsonLd(id: string, data: unknown) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/** Writes live title, description, keywords, Open Graph, Twitter and JSON-LD for every route. */
export default function SeoHead() {
  const { pathname } = useLocation();

  useEffect(() => {
    const page = resolveSeo(pathname);
    const origin = publicOrigin();
    const url = canonicalUrl(page.path);
    const image = `${origin}/og-image.png`;
    const robots = page.noIndex
      ? 'noindex, nofollow'
      : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

    document.title = page.title;
    document.documentElement.lang = 'en';

    upsertMeta('name', 'description', page.description);
    upsertMeta('name', 'keywords', page.keywords);
    upsertMeta('name', 'robots', robots);
    upsertMeta('name', 'googlebot', robots);
    upsertMeta('name', 'author', SITE_NAME);
    upsertMeta('name', 'application-name', SITE_NAME);
    upsertMeta('name', 'theme-color', '#C2185B');
    upsertMeta('name', 'apple-mobile-web-app-title', SITE_NAME);

    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:locale', 'en_IN');
    upsertMeta('property', 'og:title', page.title);
    upsertMeta('property', 'og:description', page.description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', image);
    upsertMeta('property', 'og:image:secure_url', image);
    upsertMeta('property', 'og:image:type', 'image/png');
    upsertMeta('property', 'og:image:width', '1200');
    upsertMeta('property', 'og:image:height', '630');
    upsertMeta('property', 'og:image:alt', 'Micus — business credit rating and fundraising');

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', page.title);
    upsertMeta('name', 'twitter:description', page.description);
    upsertMeta('name', 'twitter:image', image);
    upsertMeta('name', 'twitter:image:alt', 'Micus star mark — rate first, raise faster');

    upsertLink('canonical', url);
    upsertLink('icon', '/search-engine-logo.png', { type: 'image/png' });
    upsertLink('apple-touch-icon', '/search-engine-logo.png');
    upsertLink('alternate', '/llms.txt', { type: 'text/plain', title: 'llms.txt' });

    upsertJsonLd('micus-ld-org', organizationJsonLd(origin));
    upsertJsonLd('micus-ld-site', websiteJsonLd(origin));
    upsertJsonLd('micus-ld-crumb', breadcrumbJsonLd(page, origin));
    const service = serviceJsonLd(page, origin);
    if (service) upsertJsonLd('micus-ld-service', service);
    else document.getElementById('micus-ld-service')?.remove();
  }, [pathname]);

  return null;
}
